import { Close } from '@carbon/icons-react'
import { Flex } from '@chakra-ui/react'
import React, { useCallback } from 'react'
import { components } from 'react-select'

import { Select } from 'components'
import { CustomOption } from './CustomOption'

export const MultiSelectDropdown = ({
  value,
  options,
  updateFilter,
  showAllOption = false,
}: {
  value: any
  options: any[]
  updateFilter: any
  showAllOption?: boolean
}) => {
  const CustomValueContainer = ({ children, ...props }: any) => {
    const selectedValues = props.getValue()
    const count = selectedValues.length
    return (
      <components.ValueContainer
        {...props}
        ref={props.innerRef}
        whiteSpace='nowrap'
      >
        {count > 0 ? `${count} Sel.` : children}
        {/* Keep input field for typing */}
        {React.Children.map(children, (child) =>
          child &&
          child.type !== components.SingleValue &&
          child.type !== components.MultiValue
            ? child
            : null
        )}
      </components.ValueContainer>
    )
  }

  const optionsWithAll = React.useMemo(() => {
    if (!showAllOption) {
      return options
    }

    // Check if all non-disabled options are selected
    const selectableOptions = options.filter((option) => !option.disabled)
    const allSelected =
      selectableOptions.length > 0 &&
      selectableOptions.every((option) =>
        value.some((selected: any) => selected.value === option.value)
      )

    const allOption = {
      value: '__all__',
      label: 'All',
      isSelected: allSelected,
    }

    return [allOption, ...options]
  }, [options, showAllOption, value])

  const handleOnChange = useCallback(
    (selectedOptions: any[]) => {
      if (!showAllOption) {
        updateFilter(selectedOptions)
        return
      }

      const isAllSelected = selectedOptions.some(
        (option) => option.value === '__all__'
      )

      const selectableOptions = options.filter((option) => !option.disabled)
      const allCurrentlySelected =
        selectableOptions.length > 0 &&
        selectableOptions.every((option) =>
          value.some((selected: any) => selected.value === option.value)
        )

      if (isAllSelected) {
        if (allCurrentlySelected) {
          updateFilter([])
        } else {
          updateFilter(selectableOptions)
        }
      } else {
        const filteredOptions = selectedOptions.filter(
          (option) => option.value !== '__all__'
        )
        updateFilter(filteredOptions)
      }
    },
    [options, updateFilter, value, showAllOption]
  )

  return (
    <Flex w='100%' alignItems={'start'} gap={0} pos='relative' mr='20px'>
      <Select
        value={value}
        isMulti={true}
        placeholder={showAllOption ? 'Select...' : 'All'}
        isSearchable={true}
        onChange={handleOnChange}
        options={optionsWithAll}
        isOptionDisabled={(option) => option.disabled}
        borderColor='gray.400'
        backgroundColor='transparent'
        fontSize={'10px'}
        height='26px'
        width='90px'
        CustomOption={CustomOption}
        CustomValueContainer={CustomValueContainer}
        backspaceRemovesValue={false}
      />
      <Flex
        right='0'
        pos='absolute'
        bg={value.length >= 1 ? 'gray.100' : 'transparent'}
        p='1px'
        borderRightRadius={'md'}
        w='16px'
        h='29px'
        mr='4px'
        alignItems='center'
      >
        {value.length >= 1 && (
          <Close
            onClick={() => updateFilter([])}
            color='gray.500'
            cursor='pointer'
          />
        )}
      </Flex>
    </Flex>
  )
}
